import { CommonModule, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HomeButtonComponent } from '@igo2/common/home-button';
import { ResizableBarComponent } from '@igo2/common/resizable-bar';
import { IgoInteractiveTourModule, InteractiveTourLoader, InteractiveTourService } from '@igo2/common/interactive-tour';
import { PanelComponent } from '@igo2/common/panel';
import { Tool, Toolbox, ToolboxComponent } from '@igo2/common/tool';
import { ContextService } from '@igo2/context';
import { ConfigService } from '@igo2/core/config';
import { Media, MediaService } from '@igo2/core/media';
import { StorageService } from '@igo2/core/storage';
import { IgoMap, isLayerItem } from '@igo2/geo';
import { CatalogState, ToolState } from '@igo2/integration';

import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription, map } from 'rxjs';
import { importAllTools } from './sidenav-import';
import { InteractiveTourExtensionService, InteractiveTourLoaderExtensionService } from 'src/app/shared/services/interactive-tour-extension.service';
import { IconReplacementService } from 'src/app/shared/services/icon-replacement.service';

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TranslateModule,
        IgoInteractiveTourModule,
        MatButtonModule,
        MatIconModule,
        MatSidenavModule,
        MatTooltipModule,
        NgClass,
        NgIf,
        PanelComponent,
        ToolboxComponent,
        HomeButtonComponent,
        ResizableBarComponent,
    ],
    providers: [
        { provide: InteractiveTourService, useClass: InteractiveTourExtensionService },
        { provide: InteractiveTourLoader, useClass: InteractiveTourLoaderExtensionService }
    ]
})
export class SidenavComponent implements OnInit, OnDestroy {
  title$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

  private activeTool$$: Subscription;

  @Input()
  get map(): IgoMap {
    return this._map;
  }
  set map(value: IgoMap) {
    this._map = value;
  }
  private _map: IgoMap;

  @Input()
  get opened(): boolean {
    return this._opened;
  }
  set opened(value: boolean) {
    if (value === this._opened) {
      return;
    }

    this._opened = value;
    this.openedChange.emit(this._opened);
  }
  private _opened: boolean;
  /**
   * Ce variable va contenir tous les noms d'outil disponible pour un utilisateur.
   * Ainsi, l'outil d'aide n'affichera que les lignes pertinentes.
   */
  toolsAllowedAsClassesInToolContainer = '';

  @Output() openedChange = new EventEmitter<boolean>();
  @Output() toolChange = new EventEmitter<Tool>();

  get toolbox(): Toolbox {
    return this.toolState.toolbox;
  }

  /** Bornes de la largeur du menu, en px (mêmes que Forêt ouverte). */
  readonly largeurMin = 360;
  readonly largeurMax = 800;
  private static readonly CLE_LARGEUR = 'sidenavWidth';
  private memorisation: ReturnType<typeof setTimeout>;

  /** La barre de redimensionnement est masquée sur mobile, où le menu occupe déjà presque toute la largeur. */
  readonly redimensionnable$: Observable<boolean>;
  
  constructor(
    private toolState: ToolState,
    private configService: ConfigService,
    private contextService: ContextService,
    private catalogState: CatalogState,
    private iconReplacementService: IconReplacementService,
    private mediaService: MediaService,
    private storageService: StorageService,
  ) {
    importAllTools();
    this.redimensionnable$ = this.mediaService.media$.pipe(map((media) => media !== Media.Mobile));
  }

  ngOnInit() {
    const largeurMemorisee = Number(this.storageService.get(SidenavComponent.CLE_LARGEUR));
    if (largeurMemorisee) {
      this.appliquerLargeur(largeurMemorisee);
    }

    this.activeTool$$ = this.toolbox.activeTool$.subscribe((tool: Tool) => {
      const sidenavTitle = this.configService.getConfig('sidenavTitle', 'Gestion du territoire public');
      if (tool) {
        if (tool.name === 'catalogBrowser') {
          for (const catalog of this.catalogState.catalogStore.all()) {
            if (
              this.catalogState.catalogStore.state.get(catalog).selected ===
              true
            ) {
              this.title$.next(catalog.title);
            }
          }
        } else if (
          tool.name === 'activeTimeFilter' ||
          tool.name === 'activeOgcFilter'
        ) {
          for (const layer of this.map.layerController.layersFlattened) {
            if (isLayerItem(layer) && this.map.layerController.isSelected(layer)) {
              this.title$.next(layer.title);
            }
          }
        } else {
          this.title$.next(tool.title);
        }
      } else {
        this.title$.next(sidenavTitle);
      }
      this.toolChange.emit(tool);
    });

     // Ajouter les classes qui permettent la description d'un outil de s'afficher dans la section d'aide en ligne
    this.contextService.context$.subscribe(f =>
      this.toolsAllowedAsClassesInToolContainer = f?.toolbar?.map(m => 'toolbar-container-contains-' + m).join(' ') || ''
    );
  }

  ngOnDestroy() {
    this.activeTool$$.unsubscribe();
    clearTimeout(this.memorisation);
  }

  /**
   * Glisser de la barre de redimensionnement : le menu part du bord gauche de
   * la fenêtre, la position de la souris (clientX) est donc la largeur voulue.
   * La largeur est mémorisée par navigateur, une fois le glisser terminé.
   */
  onResize(event: MouseEvent): void {
    const largeur = this.appliquerLargeur(event.clientX);
    clearTimeout(this.memorisation);
    this.memorisation = setTimeout(
      () => this.storageService.set(SidenavComponent.CLE_LARGEUR, largeur),
      300
    );
  }

  /** Écrit la largeur dans --sigt-sidenav-width, lue par le menu, la barre de recherche, les contrôles et les panneaux. */
  private appliquerLargeur(largeur: number): number {
    const bornee = Math.round(Math.min(this.largeurMax, Math.max(this.largeurMin, largeur)));
    document.documentElement.style.setProperty('--sigt-sidenav-width', `${bornee}px`);
    return bornee;
  }

  onPreviousButtonClick() {
    this.toolbox.activatePreviousTool();
  }

  onUnselectButtonClick() {
    this.toolbox.deactivateTool();
  }
  


}
